import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getStageLabel } from '@vanilla/constants/Stages.js';
import { formatCurrency, formatDateDisplay, truncateString, formatPercentage, getExpectedCloseDateUrgency } from '@vanilla/utils/Utils.js';
import { getDealForecastValue } from '@vanilla/utils/ForecastUtils.js';
import { getKanbanFieldPrefs, KANBAN_FIELDS } from '@vanilla/constants/DealDisplayFields.js';
import { PipelineStore } from '@vanilla/constants/Pipelines.js';
import { Badge } from '@/components/ui/badge';
import { WithTooltip } from '@/components/ui/tooltip';
import EllipsisWithTooltip from '@/components/ui/EllipsisWithTooltip';

function formatCurrencyWithCompact(value) {
    const v = Number(value) || 0;
    if (v === 0) return { full: '$0', compact: '$0' };
    const full = formatCurrency(v, 0);
    const prefix = v < 0 ? '-' : '';
    const absValue = Math.abs(v);
    let compact;
    if (absValue >= 1000000) {
        compact = `${prefix}$${(absValue / 1000000).toFixed(1).replace(/\.0$/, '')}m`;
    } else if (absValue >= 1000) {
        compact = `${prefix}$${(absValue / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    } else {
        compact = `${prefix}$${absValue.toFixed(0)}`;
    }
    return { full, compact };
}

const FIELD_PRIORITY = ['dealAmount', 'forecastAmount', 'probability', 'expectedCloseDate', 'dealOwner', 'primaryContactName', 'dealPipeline', 'nextStep', 'status'];

function sortFieldKeys(keys) {
    return [...keys].sort((a, b) => {
        const ap = FIELD_PRIORITY.indexOf(a);
        const bp = FIELD_PRIORITY.indexOf(b);
        if (ap === -1 && bp === -1) return a.localeCompare(b);
        if (ap === -1) return 1;
        if (bp === -1) return -1;
        return ap - bp;
    });
}

function getKanbanFieldValue(deal, key) {
    switch (key) {
        case 'accountName':
            return truncateString(deal.clientName || deal.accountName, 28) || '—';
        case 'dealOwner':
            return truncateString(deal.dealOwner, 22) || '—';
        case 'dealAmount':
            return formatCurrencyWithCompact(deal.dealAmount || deal.amount || 0).full;
        case 'forecastAmount':
            return formatCurrencyWithCompact(deal.forecastAmount ?? getDealForecastValue(deal)).full;
        case 'expectedCloseDate': {
            const d = deal.expectedCloseDate || deal.lastInvoiceDate;
            return d ? formatDateDisplay(d) : '—';
        }
        case 'probability':
            return formatPercentage(deal.probability ?? 0);
        case 'primaryContactName':
            return truncateString(deal.primaryContactName, 24) || '—';
        case 'nextStep':
            return truncateString(deal.nextStep, 36) || '—';
        case 'dealPipeline':
            return PipelineStore.getLabel(deal.dealPipeline) || '—';
        case 'status':
            return deal.status || '—';
        case 'dealStage':
            return getStageLabel(deal.dealStage);
        default:
            return '—';
    }
}

function isInsightItemDone(item) {
    const status = String(item?.status || '').toLowerCase();
    return item?.is_completed === true
        || item?.is_completed === 'true'
        || item?.completed === true
        || item?.completed === 'true'
        || status === 'done'
        || status === 'completed';
}

function InsightBadge({ count }) {
    const n = Number(count) || 0;
    return (
        <Badge
            variant={n === 0 ? 'secondary' : 'default'}
            className={`kanban-insight-badge${n === 0 ? ' is-zero' : ''}`}
        >
            {n}
        </Badge>
    );
}

export default function DealCard({
    deal,
    meta = {},
    columnStage,
    onEdit,
    onInsight,
    insightCounts,
    isOverlay = false,
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: deal.id,
        disabled: isOverlay,
        data: {
            type: 'deal',
            deal,
            stage: columnStage || deal.dealStage,
        },
    });

    const style = {
        borderLeftColor: meta.color,
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.35 : undefined,
        touchAction: isOverlay || isDragging ? 'none' : 'manipulation',
        userSelect: isOverlay || isDragging ? 'none' : undefined,
        WebkitUserSelect: isOverlay || isDragging ? 'none' : undefined,
    };

    const visibleKeys = sortFieldKeys(getKanbanFieldPrefs().filter((k) => k !== 'dealStage'));
    const showStage = getKanbanFieldPrefs().includes('dealStage');
    const rating = deal.accountRating;
    const revCompletion = deal.revenueCompletionPct != null ? deal.revenueCompletionPct : null;

    const counts = insightCounts || {};
    const nextStepCount = Number.isFinite(Number(counts.nextSteps))
        ? Number(counts.nextSteps)
        : (Array.isArray(deal.nextSteps) && deal.nextSteps.length
            ? deal.nextSteps.filter((s) => !isInsightItemDone(s)).length
            : (Number.isFinite(Number(deal.pendingNextStepCount)) ? Number(deal.pendingNextStepCount) : 0));
    const taskCount = Number.isFinite(Number(counts.tasks))
        ? Number(counts.tasks)
        : (Number.isFinite(Number(deal.pendingTaskCount)) ? Number(deal.pendingTaskCount) : 0);
    const commentCount = Number.isFinite(Number(counts.comments))
        ? Number(counts.comments)
        : (Number.isFinite(Number(deal.commentCount)) ? Number(deal.commentCount) : 0);

    const onCardClick = (e) => {
        if (isDragging) return;
        if (e.target.closest('button')) return;
        onEdit?.(deal.id);
    };

    return (
        <div
            ref={isOverlay ? undefined : setNodeRef}
            className={`kanban-card${isDragging ? ' is-dragging' : ''}${isOverlay ? ' is-drag-overlay' : ''}`}
            data-deal-id={deal.id}
            data-stage={deal.dealStage}
            style={style}
            onClick={onCardClick}
            {...(isOverlay ? {} : attributes)}
            {...(isOverlay ? {} : listeners)}
        >
            <div className="kanban-card-drag-hint" aria-hidden="true">⠿</div>
            <div className={`kanban-card-content ${rating ? `kanban-card-rating-${rating}` : ''}`}>
                <EllipsisWithTooltip
                    className="kanban-card-title"
                    text={deal.dealName || deal.name || ''}
                />
                {showStage && (
                    <div className="kanban-card-stage">
                        <span className="kanban-stage-dot" style={{ backgroundColor: meta.color }}></span>
                        {getStageLabel(deal.dealStage)}
                    </div>
                )}
                {visibleKeys.length ? visibleKeys.map((key) => {
                    const def = KANBAN_FIELDS.find((f) => f.key === key);
                    const label = def?.label || key;
                    const value = getKanbanFieldValue(deal, key);
                    const isEmpty = !value || value === '—';
                    const extraClass = key === 'dealAmount' ? ' kanban-card-amount-row' : key === 'forecastAmount' ? ' kanban-card-forecast-row' : '';
                    const dateStr = key === 'expectedCloseDate' ? (deal.expectedCloseDate || deal.lastInvoiceDate) : '';
                    const urgency = dateStr ? getExpectedCloseDateUrgency(dateStr, deal.dealStage) : '';
                    return (
                        <div className={`kanban-card-row${extraClass}${isEmpty ? ' is-empty' : ''}`} key={key}>
                            <span className="card-label">{label}</span>
                            <span className={`card-value${key === 'dealAmount' ? ' amount' : ''}${key === 'forecastAmount' ? ' forecast' : ''}`}>
                                {isEmpty
                                    ? <span className="kanban-empty-value">—</span>
                                    : (key === 'expectedCloseDate' && urgency
                                        ? <span className={`close-date-${urgency}`}>{dateStr ? formatDateDisplay(dateStr) : value}</span>
                                        : value)}
                            </span>
                        </div>
                    );
                }) : (
                    <div className="kanban-card-row is-empty"><span className="card-label">&nbsp;</span><span className="card-value">No additional fields selected</span></div>
                )}
            </div>
            <div className="kanban-card-actions">
                {revCompletion != null && (
                    <span className={`kanban-card-revenue-completion ${revCompletion === 0 ? 'rc-zero' : ''}`} title="Revenue completion">RC: {revCompletion}%</span>
                )}
                <div className="kanban-card-insights" role="group" aria-label="Deal quick actions">
                    <WithTooltip
                        content="Preview pending next steps"
                        contentClassName="text-[length:var(--font-caption)] px-[10px] py-[6px]"
                    >
                        <button
                            type="button"
                            className="card-action-btn kanban-insight-btn"
                            aria-label="Pending next steps"
                            data-insight-kind="nextsteps"
                            onClick={(e) => {
                                e.stopPropagation();
                                onInsight?.(e.currentTarget, deal, 'nextsteps');
                            }}
                        >
                            <span className="kanban-insight-icon" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 8h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /><path d="M7.5 4.8L11 8l-3.5 3.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </span>
                            <InsightBadge count={nextStepCount} />
                        </button>
                    </WithTooltip>
                    <WithTooltip
                        content="Preview pending tasks"
                        contentClassName="text-[length:var(--font-caption)] px-[10px] py-[6px]"
                    >
                        <button
                            type="button"
                            className="card-action-btn kanban-insight-btn"
                            aria-label="Pending tasks"
                            data-insight-kind="tasks"
                            onClick={(e) => {
                                e.stopPropagation();
                                onInsight?.(e.currentTarget, deal, 'tasks');
                            }}
                        >
                            <span className="kanban-insight-icon" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="2.5" y="2.5" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="1.2" /><path d="M5 8l1.7 1.9L11.2 5.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </span>
                            <InsightBadge count={taskCount} />
                        </button>
                    </WithTooltip>
                    <WithTooltip
                        content="Comments & timeline"
                        contentClassName="text-[length:var(--font-caption)] px-[10px] py-[6px]"
                    >
                        <button
                            type="button"
                            className="card-action-btn kanban-insight-btn"
                            aria-label="Comments and timeline"
                            data-insight-kind="comments"
                            onClick={(e) => {
                                e.stopPropagation();
                                onInsight?.(e.currentTarget, deal, 'comments');
                            }}
                        >
                            <span className="kanban-insight-icon" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3.2 3.1h9.6a1.7 1.7 0 0 1 1.7 1.7v5.3a1.7 1.7 0 0 1-1.7 1.7H8l-2.8 2.2c-.5.4-1.2 0-1.2-.6v-1.6H3.2a1.7 1.7 0 0 1-1.7-1.7V4.8a1.7 1.7 0 0 1 1.7-1.7Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" /><path d="M4.8 6.6h6.4M4.8 8.9h4.3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>
                            </span>
                            <InsightBadge count={commentCount} />
                        </button>
                    </WithTooltip>
                    <WithTooltip
                        content="Copy deal link"
                        contentClassName="text-[length:var(--font-caption)] px-[10px] py-[6px]"
                    >
                        <button
                            type="button"
                            className="card-action-btn kanban-insight-btn"
                            aria-label="Copy deal link"
                            onClick={(e) => {
                                e.stopPropagation();
                                import('../lib/dealLinks.js').then(({ copyDealLink }) => copyDealLink(deal.id));
                            }}
                        >
                            <span className="kanban-insight-icon" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6.5 9.5a3 3 0 0 0 4.24.1l1.66-1.66a3 3 0 0 0-4.24-4.24L7.5 4.36" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /><path d="M9.5 6.5a3 3 0 0 0-4.24-.1L3.6 8.06a3 3 0 0 0 4.24 4.24L8.5 11.64" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>
                            </span>
                        </button>
                    </WithTooltip>
                </div>
            </div>
        </div>
    );
}
